import { QRCodeCanvas } from 'qrcode.react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { useToast } from '../../../components/Toast';
import { Dropdown } from 'primereact/dropdown';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import "jspdf-autotable";
import { TieredMenu } from "primereact/tieredmenu";
import { exportExcel, exportQrPDF } from "../../../components/Export";
import { Sidebar } from 'primereact/sidebar';
import { Fieldset } from 'primereact/fieldset';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import assignmentService from './assignment-service';
import { LayoutContext } from '../../../layout/context/LayoutContext';
import { LayoutConfig } from '../../../types/types';
import Layout from '../../../layout/layout';
import RequiredLabel from '../../../components/RequiredLabel';
import { MCHexceptions } from '../MCHexceptions';
import { useDialog } from '../../../components/Dialog';
import { Dialog } from 'primereact/dialog';
import { msalInstance } from '../../../utils/msalConfig';

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    'fullName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    representative: { value: null, matchMode: FilterMatchMode.IN },
    date: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    balance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};

interface RecordFilter {
    [key: string]: string | boolean | Date; // Adjust types as needed
}


const AssignmentEdit: React.FC = () => {
    const navigate = useNavigate();
    const [clientState, setClientState] = useState<any>(null);
    const { showToast } = useToast();
    const [formData, setFormData] = useState<any>(null);
    const [qrData, setQrData] = useState<any>(null);
    const { showExceptionDialog, showConfirmDialog } = useDialog();
    const [displayConfirmation, setDisplayConfirmation] = useState(false);
    const [showAlreadyAssignedDialog, setShowAlreadyAssignedDialog] = useState(false);
    const [showCountryDialog, setShowCountryDialog] = useState(false);

    const [instruments, setInstruments] = useState<any[]>([]);
    const [selectedInstrument, setSelectedInstrument] = useState<any | null>(null);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const requiredFields = ["snNo"];

    const { param } = useParams();

    const [entityDropdown, setEntityDropdown] = useState<any[]>([]);
    const entityDropdownOptions = useMemo(() => entityDropdown, [entityDropdown]);

    const defaultData = {
        snNo: "",
        entity: "",
    };

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await assignmentService.getClientState();
                setClientState(clientStateItem);

                if (!clientStateItem.country) {
                    setShowCountryDialog(true);
                }

                assignmentService.findOneQrdet(param).then((data) => {
                    setQrData(data);
                });

                assignmentService.getDropdown('AST', 'ENTITY').then((data) => {
                    setEntityDropdown(data);
                });

                setFormData(defaultData);
            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    const handleUpperCaseInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value.toUpperCase(),
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };



    const checkInstrument = (e: any) => {

        let newErrors: { [key: string]: string } = {};
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = "This is a required field.";
                showToast(clientState.required);
            }
        });

        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
            if (formData.snNo) {
                assignmentService.findAllInstrumentBySN(formData.snNo).then((data) => {
                    if (data) {
                        setInstruments(data);
                        if (data.length === 1) {
                            setSelectedInstrument(data[0]);
                        }
                    } else {
                        showExceptionDialog(103, MCHexceptions);
                        setInstruments([]);
                        setFormData({
                            ...formData,
                            snNo: null,
                        });
                    }

                });
            }
        }
    };

    const confirmationDialogFooter = () => {


        return (
            <>

            </>
        )
    };

    const footerTemplate = () => {
        function submit(): void {
            let newErrors: { [key: string]: string } = {};
            requiredFields.forEach((field) => {
                if (!formData[field]) {
                    newErrors[field] = "This is a required field.";
                    showToast(clientState.required);
                }
            });
            setErrors(newErrors);

            if (Object.keys(newErrors).length === 0) {
                if (!selectedInstrument) {
                    showExceptionDialog(104, MCHexceptions);
                } else {
                    showConfirmDialog("Confirmation", "Are you sure you want to save this?", () => {
                        assignmentService.findOneQrByInstrumentId(selectedInstrument.instrumentId).then((data) => {
                            if (data?.qrId) {
                                setShowAlreadyAssignedDialog(true);
                            }
                            else {
                                const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

                                qrData.instrumentId = selectedInstrument?.instrumentId;
                                qrData.assignBy = clientState.fullName;
                                qrData.assignDate = new Date(currentDate);
                                qrData.snNo = selectedInstrument?.snNo;
                                qrData.userId = clientState.fullName;
                                qrData.facilityCode = clientState.facilityCode;


                                assignmentService.assign(qrData).then(() => {
                                    showToast(clientState.success);
                                    setDisplayConfirmation(true);
                                }).catch((error) => {
                                    clientState.error.detail = error.response?.data?.message || 'Failed to create record.';
                                    showToast(clientState.error);
                                });
                            }
                        });
                    })
                };
            }
        }

        function handleBack(): void {
            navigate(`/login`)
        }

        async function confirmAssign(): Promise<void> {
            setDisplayConfirmation(false);
            localStorage.clear();
            sessionStorage.clear();
            window.location.href = "/login";
        };

        return (
            <>
                <Button label="Assign" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={submit} />
                <Button label="Cancel" icon="pi pi-times" style={{ marginRight: '.5em' }} onClick={() => handleBack()} />

                <Dialog
                    header="Confirmation"
                    visible={displayConfirmation}
                    onHide={() => setDisplayConfirmation(false)}
                    style={{ width: '350px' }}
                    modal
                >
                    Instrument Already Assign
                    <div>
                        <br />
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Button outlined type="button" onClick={confirmAssign}>
                            OK
                        </Button>
                    </div>

                </Dialog>

            </>
        );
    };



    return (
        <div className='full-screen-container'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>QR Assignment</h5>
                </div>
                <div className='form-title'>
                    <Fieldset legend="QR Assignment Information">
                        <div className="p-fluid form-grid-edit grid">
                            {/* <div className="f-field">
                                <RequiredLabel text="Entity" required />
                                <div className="f-input">
                                    <Dropdown
                                        id="entity"
                                        value={formData?.entity}
                                        onChange={handleDropdownChange}
                                        options={entityDropdownOptions}
                                        optionLabel="fullDesc"
                                        optionValue="referenceCode"
                                        placeholder="Select One"
                                        showClear
                                    />
                                    {errors.entity && <small className="p-error">{errors.entity}</small>}
                                </div>
                            </div> */}
                            {/* <div className="f-field"></div> */}
                            <div className="f-field">
                                <RequiredLabel text="Serial Number" required />
                                <div className="f-input">
                                    <div className="flex gap-2">
                                        <InputText
                                            id="snNo"
                                            value={formData?.snNo || ''}
                                            onChange={handleUpperCaseInputChange}
                                        />
                                        <Button
                                            icon="pi pi-search"
                                            className="p-button-warning"
                                            onClick={checkInstrument}
                                        />
                                    </div>
                                    {errors.snNo && <small className="p-error">{errors.snNo}</small>}
                                </div>

                            </div>

                        </div>
                    </Fieldset>

                    {instruments.length > 0 ? (
                        <>
                            <div>
                                <br />
                            </div>
                            <Fieldset legend="Instrument Information">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {instruments.map((instrument, index) => (
                                        <div key={index} className="flex align-items-center mb-3 w-full">

                                            <input
                                                type="radio"
                                                className="mr-3 custom-radio"
                                                checked={selectedInstrument?.instrumentId === instrument.instrumentId}
                                                onChange={() => setSelectedInstrument(instrument)}
                                            />

                                            <div
                                                className={`instrument-card border-round border-1 cursor-pointer ${selectedInstrument?.instrumentId === instrument.instrumentId
                                                    ? 'selected'
                                                    : ''}`}
                                                onClick={() => setSelectedInstrument(instrument)}
                                            >
                                                <div className={`card-header-instrument flex justify-content-between align-items-center px-2 py-2 ${selectedInstrument?.instrumentId === instrument.instrumentId
                                                    ? 'selected'
                                                    : ''}`}>
                                                    <div className="flex align-items-center gap-2">
                                                        <span className="font-semibold text-lg">
                                                            {instrument.itemDesc || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="p-fluid form-grid-edit grid p-3">
                                                    <div className="f-view">
                                                        <label className="f-label">Serial No</label>
                                                        <div className="f-input">{instrument?.snNo || 'N/A'}</div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Instrument Code</label>
                                                        <div className="f-input">{instrument?.itemCode || 'N/A'}</div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Instrument Name</label>
                                                        <div className="f-input">{instrument?.itemDesc || 'N/A'}</div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Manufacturer Name</label>
                                                        <div className="f-input">{instrument?.manufacturerName || 'N/A'}</div>
                                                    </div>

                                                    {instrument?.entity !== 'BMSG' && (
                                                        <div className="f-view">
                                                            <label className="f-label">Region</label>
                                                            <div className="f-input">
                                                                {instrument?.regionHasOne?.fullDesc || 'N/A'}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="f-view">
                                                        <label className="f-label">Equipment Type</label>
                                                        <div className="f-input">
                                                            {instrument?.equipType?.toUpperCase() || 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Entity</label>
                                                        <div className="f-input">{instrument?.entity || 'N/A'}</div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Last PM Date</label>
                                                        <div className="f-input">
                                                            {instrument?.lastPmDate
                                                                ? new Date(instrument.lastPmDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                                : 'N/A'}
                                                        </div>
                                                    </div>
                                                    <div className="f-view">
                                                        <label className="f-label">Next PM Date</label>
                                                        <div className="f-input">
                                                            {instrument?.nextPmDate
                                                                ? new Date(instrument.nextPmDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                                : 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Installation Date</label>
                                                        <div className="f-input">
                                                            {instrument?.installDate
                                                                ? new Date(instrument.installDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                                : 'N/A'}
                                                        </div>
                                                    </div>
                                                    <div className="f-view">
                                                        <label className="f-label">Warranty End Date</label>
                                                        <div className="f-input">
                                                            {instrument?.warrantyEndDate
                                                                ? new Date(instrument.warrantyEndDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                                : 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Contract Start Date</label>
                                                        <div className="f-input">
                                                            {instrument?.contractStartDate
                                                                ? new Date(instrument.contractStartDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                                : 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Contract End Date</label>
                                                        <div className="f-input">
                                                            {instrument?.contractEndDate
                                                                ? new Date(instrument.contractEndDate).toLocaleDateString('en-GB', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })
                                                                : 'N/A'}
                                                        </div>
                                                    </div>

                                                    <div className="f-view">
                                                        <label className="f-label">Installation Location</label>
                                                        <div className="f-input">
                                                            {instrument?.installationLocation || 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Fieldset>
                            <div>
                                <br />
                            </div>
                            <Toolbar end={footerTemplate} />
                        </>
                    ) : (
                        <div></div>
                    )}

                </div>
            </div >
            <Dialog
                header="Business Rule"
                visible={showAlreadyAssignedDialog}
                onHide={() => setShowAlreadyAssignedDialog(false)}
                style={{ width: '450px' }}
                modal
            >
                <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                    {"Instrument Has Been Assigned to Another Instrument.\nPlease contact Service Admin."}
                </div>

                <div style={{ marginTop: '1rem', display: "flex", justifyContent: "center" }}>
                    <Button
                        label="OK"
                        icon="pi pi-check"
                        onClick={() => setShowAlreadyAssignedDialog(false)}
                        autoFocus
                    />
                </div>
            </Dialog>
            <Dialog
                header="Business Rule"
                visible={showCountryDialog}
                onHide={() => setShowCountryDialog(false)}
                style={{ width: '450px' }}
                modal
            >
                <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                    {"Please update your country in Microsoft Account before using the system.\nContact IT support for assistance."}
                </div>

                <div style={{ marginTop: '1rem', display: "flex", justifyContent: "center" }}>
                    <Button
                        label="OK"
                        icon="pi pi-check"
                        onClick={() => setShowCountryDialog(false)}
                        autoFocus
                    />
                </div>
            </Dialog>
        </div>

    );
};

export default AssignmentEdit;

